import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ExternalLink, Shield, Briefcase, Scale, DollarSign, GraduationCap, Heart, ChevronRight, ArrowLeft, Star, Phone, BookOpen } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

const CATEGORIES = [
  {
    id: 'active-duty',
    title: 'While You Serve',
    icon: Shield,
    color: 'from-blue-600 to-blue-700',
    badgeColor: 'bg-blue-100 text-blue-800',
    description: 'Decisions and benefits that impact your career and future'
  },
  {
    id: 'transition',
    title: 'Transition & Separation',
    icon: Briefcase,
    color: 'from-amber-600 to-amber-700',
    badgeColor: 'bg-amber-100 text-amber-800',
    description: 'Navigate your path from military to civilian life'
  },
  {
    id: 'disability',
    title: 'VA Disability & Claims',
    icon: Scale,
    color: 'from-red-600 to-red-700',
    badgeColor: 'bg-red-100 text-red-800',
    description: 'File claims, understand ratings, and maximize your benefits'
  },
  {
    id: 'pay-retirement',
    title: 'Pay & Retirement',
    icon: DollarSign,
    color: 'from-green-600 to-green-700',
    badgeColor: 'bg-green-100 text-green-800',
    description: 'Retirement planning, pay calculations, and financial tools'
  },
  {
    id: 'planning-housing',
    title: 'Planning & Housing',
    icon: GraduationCap,
    color: 'from-purple-600 to-purple-700',
    badgeColor: 'bg-purple-100 text-purple-800',
    description: 'Education, home loans, financial planning, and state benefits'
  },
  {
    id: 'family-health',
    title: 'Family & Health',
    icon: Heart,
    color: 'from-pink-600 to-pink-700',
    badgeColor: 'bg-pink-100 text-pink-800',
    description: 'Healthcare, family support, insurance, and caregiver resources'
  }
];

const RESOURCES = [
  // While You Serve
  { id: 1, category: 'active-duty', title: 'First Days in Uniform — 7 Key Decisions', description: 'Seven benefits decisions from your first weeks of service that follow you for decades. GI Bill buy-in, SGLI, TSP, documenting injuries, SCRA protections, and more.', url: 'https://www.page214.org/first-days-in-uniform.html', type: 'guide', tags: ['new service', 'sgli', 'tsp', 'gi bill'] },
  { id: 2, category: 'active-duty', title: 'Deployment Benefits Checklist', description: 'Complete checklist for deployers — SGLI review, TSP Roth combat zone (triple tax-free), Savings Deposit Program (10% guaranteed), SCRA, combat pay exclusions, and more.', url: 'https://www.page214.org/deployment-guide.html', type: 'guide', tags: ['deployment', 'combat zone', 'sdp', 'tax free'] },
  { id: 3, category: 'active-duty', title: 'PCS Entitlements Checklist', description: 'Know what you\'re owed during a PCS — DLA, per diem, HHG weight allowances, TLE/TLA, PPM/DITY moves, advance pay, mileage, and POV shipment.', url: 'https://www.page214.org/pcs-entitlements.html', type: 'tool', tags: ['pcs', 'moving', 'dla', 'dity'] },
  { id: 4, category: 'active-duty', title: 'Life Events Benefits Checklist', description: 'Marriage, baby, divorce, death, promotion — every life event triggers actions across DEERS, TRICARE, SGLI, TSP, VA, and finance.', url: 'https://www.page214.org/life-events.html', type: 'tool', tags: ['marriage', 'baby', 'divorce', 'deers'] },
  { id: 5, category: 'active-duty', title: 'Military Pay Calculator 2026', description: 'Calculate total compensation: base pay, BAH by location, BAS, special pays, tax advantage, and civilian salary equivalent for all ranks.', url: 'https://www.page214.org/military-pay-calculator.html', type: 'calculator', tags: ['pay', 'bah', 'bas', 'compensation'] },
  { id: 6, category: 'active-duty', title: 'Special & Incentive Pay Guide', description: 'Every special pay, incentive pay, and bonus — medical ISP, aviation, nuclear, dive, jump, sub pay, FLPP, SDAP, and enlistment bonuses by branch.', url: 'https://www.page214.org/special-pay-guide.html', type: 'guide', tags: ['special pay', 'bonus', 'incentive'] },

  // Transition & Separation
  { id: 7, category: 'transition', title: 'MEB / PEB & IDES Guide', description: 'Complete Medical/Physical Evaluation Board guide — 9-step timeline, outcomes (PDRL, TDRL, separation), severance calculator, and your rights.', url: 'https://www.page214.org/meb-peb-ides-guide.html', type: 'guide', tags: ['meb', 'peb', 'ides', 'medical board'] },
  { id: 8, category: 'transition', title: 'Separation & Retirement Timeline', description: 'Interactive countdown from 24 months out through 1 year post-separation with action items, deadlines, and branch-specific requirements.', url: 'https://www.page214.org/separation-timeline.html', type: 'tool', tags: ['separation', 'retirement', 'timeline', 'tap'] },
  { id: 9, category: 'transition', title: 'DD-214 Benefits Decoder', description: 'Find out which VA benefits you qualify for based on discharge characterization — Honorable, General, OTH, and upgrade options.', url: 'https://www.page214.org/dd214-benefits-decoder.html', type: 'tool', tags: ['dd-214', 'discharge', 'eligibility'] },
  { id: 10, category: 'transition', title: 'Discharge Upgrade Guide', description: 'How to upgrade a less-than-honorable discharge — DRB, BCMR/BCNR, Hagel/Kurta/Wilkie memos, DADT upgrades, and OTH eligibility.', url: 'https://www.page214.org/discharge-upgrade.html', type: 'guide', tags: ['discharge upgrade', 'oth', 'bcmr'] },
  { id: 11, category: 'transition', title: 'MOS to Civilian Career Translator', description: 'Translate your MOS, rating, or AFSC into civilian careers with salary ranges, certifications, and DoD COOL funding info.', url: 'https://www.page214.org/mos-translator.html', type: 'tool', tags: ['mos', 'career', 'civilian', 'translator'] },
  { id: 12, category: 'transition', title: 'Federal Hiring Toolkit', description: 'Navigate federal employment — resume rules, veteran hiring authorities (VRA, VEOA), GS-level targeting, and military-to-federal translation.', url: 'https://www.page214.org/federal-resume-translator.html', type: 'tool', tags: ['federal', 'resume', 'usajobs', 'hiring'] },
  { id: 13, category: 'transition', title: 'Education Benefits Calculator', description: 'Compare Post-9/11 GI Bill, Montgomery GI Bill, VR&E, Chapter 35, and more — with Rudisill entitlement and transfer rules.', url: 'https://www.page214.org/education-benefits-calculator.html', type: 'calculator', tags: ['gi bill', 'education', 'vr&e', 'chapter 33'] },
  { id: 14, category: 'transition', title: 'Returning to Service Guide', description: 'What happens to VA comp, GI Bill, retirement credits, and TRICARE if you go back in — prior-service options and commissioning paths.', url: 'https://www.page214.org/returning-to-service.html', type: 'guide', tags: ['return to service', 'prior service', 'ocs'] },

  // VA Disability & Claims
  { id: 15, category: 'disability', title: 'Top VA Disability Claims — Rating Criteria', description: 'Look up rating criteria for 150+ conditions from 38 CFR Part 4 — plain-language breakdown of every rating tier and expected percentages.', url: 'https://www.page214.org/top-claims.html', type: 'tool', tags: ['rating', 'criteria', 'conditions', 'cfr'] },
  { id: 16, category: 'disability', title: 'VSO & Representatives Guide', description: 'Understand VSO reps (free), claims agents, and attorneys — how to find one, when to use each, and red flags to avoid.', url: 'https://www.page214.org/vso-guide.html', type: 'guide', tags: ['vso', 'representative', 'claims agent'] },
  { id: 17, category: 'disability', title: 'VA Disability Rating Calculator', description: 'Calculate combined VA rating using VA math, bilateral factor, and Special Monthly Compensation with 2026 rates.', url: 'https://www.page214.org/va-disability-rating-calculator.html', type: 'calculator', tags: ['rating', 'calculator', 'smc', 'combined'] },
  { id: 18, category: 'disability', title: 'How to File a VA Claim — Step by Step', description: 'Complete guide for all 11 claim types — direct, presumptive, secondary, increase, supplemental, HLR, BVA, BDD, MST, and TDIU.', url: 'https://www.page214.org/va-claim-guide.html', type: 'guide', tags: ['file claim', 'va claim', 'bdd', 'appeal'] },
  { id: 19, category: 'disability', title: 'Presumptive Conditions Lookup', description: 'Search the VA\'s full presumptive list — PACT Act, Agent Orange, burn pits, Gulf War illness, radiation, Camp Lejeune, and POW conditions.', url: 'https://www.page214.org/presumptive-conditions.html', type: 'tool', tags: ['presumptive', 'pact act', 'burn pit', 'agent orange'] },
  { id: 20, category: 'disability', title: 'Nexus & Service Connection Guide', description: 'How to argue service connection for 30+ conditions with nexus theories, evidence checklists, and sample nexus letter template.', url: 'https://www.page214.org/nexus-guide.html', type: 'guide', tags: ['nexus', 'service connection', 'evidence'] },
  { id: 21, category: 'disability', title: 'VA Claim Processing Timeline', description: 'Track your claim with projected dates for initial, FDC, BDD, supplemental, HLR, and all 3 BVA dockets.', url: 'https://www.page214.org/va-claim-processing-timeline.html', type: 'tool', tags: ['timeline', 'processing', 'wait time'] },
  { id: 22, category: 'disability', title: 'VA Back Pay Calculator', description: 'Calculate retroactive compensation from effective date to decision date with COLA-accurate 2023-2026 rates.', url: 'https://www.page214.org/va-back-pay-calculator.html', type: 'calculator', tags: ['back pay', 'retroactive', 'effective date'] },
  { id: 23, category: 'disability', title: 'TDIU Eligibility Estimator', description: 'Check if your disabilities qualify for compensation at the 100% rate — schedular criteria, employment rules, and evidence guidance.', url: 'https://www.page214.org/tdiu-estimator.html', type: 'tool', tags: ['tdiu', 'unemployability', '100 percent'] },
  { id: 24, category: 'disability', title: '100% VA Benefits Guide', description: 'Complete guide to 25 federal benefits for 100% Schedular, P&T, and TDIU veterans — CHAMPVA, DEA, DIC, and more.', url: 'https://www.page214.org/100-percent-va-benefits-guide.html', type: 'guide', tags: ['100 percent', 'p&t', 'champva'] },

  // Pay & Retirement
  { id: 25, category: 'pay-retirement', title: 'Military Retirement Pay Calculator', description: 'All 4 retirement systems plus Reserve/Guard — CRDP vs CRSC, SBP deductions, VA offset, and federal tax estimates.', url: 'https://www.page214.org/military-retirement-pay-calculator.html', type: 'calculator', tags: ['retirement', 'pension', 'brs', 'high-3'] },
  { id: 26, category: 'pay-retirement', title: 'Guard & Reserve Benefits Navigator', description: 'Complete benefits hub — eligibility by activation type, retirement calculator, TRS healthcare, education, and early retirement credits.', url: 'https://www.page214.org/reserve-retirement-calculator.html', type: 'tool', tags: ['guard', 'reserve', 'points', 'trs'] },
  { id: 27, category: 'pay-retirement', title: 'Stay or Retire Financial Comparison', description: 'Compare active duty income vs retire + civilian career — promotions, TSP, taxes, second career runway analysis.', url: 'https://www.page214.org/stay-vs-retire-calculator.html', type: 'calculator', tags: ['stay vs retire', 'financial', 'comparison'] },
  { id: 28, category: 'pay-retirement', title: 'CRDP vs CRSC Calculator', description: 'Compare Concurrent Retirement & Disability Pay vs Combat-Related Special Compensation — side-by-side with VA offset.', url: 'https://www.page214.org/crdp-crsc-calculator.html', type: 'calculator', tags: ['crdp', 'crsc', 'concurrent', 'combat'] },
  { id: 29, category: 'pay-retirement', title: 'Military Time Buyback Calculator', description: 'Buy back military time for FERS/CSRS — deposit cost, pension increase, breakeven, lifetime ROI, and cost of waiting.', url: 'https://www.page214.org/military-buyback-calculator.html', type: 'calculator', tags: ['buyback', 'fers', 'csrs', 'federal'] },
  { id: 30, category: 'pay-retirement', title: 'TSP Guide for Military', description: 'BRS matching, Roth vs Traditional, combat zone advantage, fund selection, 2026 limits, and post-separation options.', url: 'https://www.page214.org/tsp-guide.html', type: 'guide', tags: ['tsp', 'thrift savings', 'roth', 'matching'] },

  // Financial Planning & Housing
  { id: 31, category: 'planning-housing', title: 'Long-Term Wealth Projection', description: 'Lifetime financial roadmap — military pay, TSP, pension, VA disability, civilian career, Social Security projected through retirement.', url: 'https://www.page214.org/wealth-projection-calculator.html', type: 'calculator', tags: ['wealth', 'projection', 'financial plan'] },
  { id: 32, category: 'planning-housing', title: 'VA Home Loan Calculator', description: 'Estimate affordability, monthly payment, funding fee, and VA vs Conventional vs FHA comparison with IRRRL refinance calculator.', url: 'https://www.page214.org/va-home-loan-calculator.html', type: 'calculator', tags: ['home loan', 'va loan', 'mortgage', 'irrrl'] },
  { id: 33, category: 'planning-housing', title: 'State Veteran Benefits Comparison', description: 'All 50 states + DC + Puerto Rico — property tax exemptions, income tax, education, vehicle benefits. Compare up to 5 states.', url: 'https://www.page214.org/state-veteran-benefits.html', type: 'tool', tags: ['state benefits', 'property tax', 'comparison'] },
  { id: 34, category: 'planning-housing', title: 'Veterans Overseas Benefits Guide', description: 'Which benefits continue worldwide, which change, and which stop — FMP, taxes, C&P exams abroad, and relocation checklist.', url: 'https://www.page214.org/overseas-benefits.html', type: 'guide', tags: ['overseas', 'abroad', 'fmp', 'expat'] },
  { id: 35, category: 'planning-housing', title: 'SCRA & MLA Protections Guide', description: '6% interest cap, foreclosure protection, lease termination, eviction protection, 36% MLA lending cap — what expires when you separate.', url: 'https://www.page214.org/scra-mla-guide.html', type: 'guide', tags: ['scra', 'mla', 'interest cap', 'protection'] },

  // Family, Health & Support
  { id: 36, category: 'family-health', title: 'Healthcare Comparison Guide', description: 'TRICARE Prime vs Select vs TRS vs VA Healthcare — 2026 costs, copays, pharmacy, and coverage compared across all plans.', url: 'https://www.page214.org/healthcare-comparison-guide.html', type: 'tool', tags: ['tricare', 'healthcare', 'prime', 'select'] },
  { id: 37, category: 'family-health', title: 'Women Veterans Benefits & Healthcare', description: 'Maternity care, MST support, mammography, Women Veteran Health Coordinators, gender-specific claims, and family planning.', url: 'https://www.page214.org/women-veterans.html', type: 'guide', tags: ['women', 'maternity', 'mst'] },
  { id: 38, category: 'family-health', title: 'Life Insurance Comparison', description: 'Compare SGLI, VGLI, VALife, FSGLI, TSGLI, S-DVI, and VMLI — premium calculator, deadlines, and layering strategy.', url: 'https://www.page214.org/life-insurance-comparison.html', type: 'tool', tags: ['sgli', 'vgli', 'valife', 'insurance'] },
  { id: 39, category: 'family-health', title: 'Survivor Benefits Guide', description: 'DIC, Survivors Pension, SBP, education, CHAMPVA, home loans, and burial benefits for Gold Star families and surviving spouses.', url: 'https://www.page214.org/gold-star-family-benefits.html', type: 'guide', tags: ['survivor', 'dic', 'gold star', 'sbp'] },
  { id: 40, category: 'family-health', title: 'Caregiver Eligibility Navigator', description: 'PCAFC eligibility, stipend estimator (up to ~$3,900/mo tax-free), CHAMPVA, respite care, and application guidance.', url: 'https://www.page214.org/caregiver-navigator.html', type: 'tool', tags: ['caregiver', 'pcafc', 'stipend'] },
  { id: 41, category: 'family-health', title: 'Aid & Attendance & Elderly Care', description: 'Benefits for aging veterans — Aid & Attendance payments, VA nursing homes, home health aides, respite care, and PACE programs.', url: 'https://www.page214.org/aid-attendance-elderly-care.html', type: 'guide', tags: ['aid attendance', 'elderly', 'nursing home'] },
  { id: 42, category: 'family-health', title: 'VA Debt & Overpayment Guide', description: 'Got a VA debt letter? Waiver, dispute, compromise, repayment plans, critical deadlines, and consequences of inaction.', url: 'https://www.page214.org/va-debt-overpayment-guide.html', type: 'guide', tags: ['debt', 'overpayment', 'waiver'] },
  { id: 43, category: 'family-health', title: 'Veteran Charity Finder', description: 'Find trusted, highly-rated veteran charities by focus area — PTSD, housing, employment, families, and more.', url: 'https://www.page214.org/vetcompass.html', type: 'tool', tags: ['charity', 'nonprofit', 'donation'] },
  { id: 44, category: 'family-health', title: 'Military Spouse Guide', description: 'DEERS, TRICARE, the LES, SCRA protections, PCS moves, deployment checklists, SBP, GI Bill transfer — in plain language.', url: 'https://www.page214.org/military-spouse-guide.html', type: 'guide', tags: ['spouse', 'family', 'mycaa', 'deers'] },
  { id: 45, category: 'family-health', title: 'Service Dogs & Military Working Animals', description: 'Service dogs for PTSD and mobility, VA veterinary benefits, PAWS Act, service dogs vs ESAs, and how to obtain one.', url: 'https://www.page214.org/service-animals-guide.html', type: 'guide', tags: ['service dog', 'ptsd', 'paws act'] },
  { id: 46, category: 'family-health', title: 'Military Campaigns & Benefits', description: 'Every major conflict mapped to VA presumptive conditions, toxic exposure programs, campaign medals, and special eligibility.', url: 'https://www.page214.org/campaigns-guide.html', type: 'guide', tags: ['campaigns', 'gwot', 'vietnam', 'gulf war'] },
  { id: 47, category: 'family-health', title: 'Essential Military & VA Documents', description: '16 documents every veteran needs — DD-214, STRs, C-file, COE, NGB-22, VMET, JST, veteran IDs, and how to request each.', url: 'https://www.page214.org/documents-guide.html', type: 'guide', tags: ['documents', 'dd-214', 'records', 'c-file'] },
  { id: 48, category: 'family-health', title: 'Philippines & US Veterans Guide', description: 'Manila VA facility, Filipino WWII veterans, benefits for veterans in the Philippines, SRRV visa, and practical guidance.', url: 'https://www.page214.org/philippines-veterans-guide.html', type: 'guide', tags: ['philippines', 'manila', 'overseas'] },
];

const QUICK_REFERENCES = [
  { title: 'Benefits Finder', description: 'Answer 5 questions to see every benefit you\'ve earned', url: 'https://www.page214.org/benefits-finder.html', icon: Star },
  { title: 'VA & Military Glossary', description: '56 terms in plain English', url: 'https://www.page214.org/glossary.html', icon: BookOpen },
  { title: 'Crisis Resources', description: '988 (press 1), MST support, homeless vets hotlines', url: 'https://www.page214.org/crisis.html', icon: Phone },
  { title: "Veteran's Guide", description: 'The big picture — start to finish', url: 'https://www.page214.org/veterans-guide.html', icon: BookOpen },
];

const ResourceCard = ({ resource }) => {
  const typeColors = {
    calculator: 'bg-green-100 text-green-700 border-green-200',
    tool: 'bg-blue-100 text-blue-700 border-blue-200',
    guide: 'bg-amber-100 text-amber-700 border-amber-200',
  };

  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`resource-card-${resource.id}`}
      className="group block"
    >
      <Card className="h-full border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 bg-white">
        <CardContent className="p-4 flex flex-col h-full">
          <div className="flex items-start justify-between mb-2">
            <Badge className={`text-xs font-medium border ${typeColors[resource.type] || typeColors.guide}`}>
              {resource.type}
            </Badge>
            <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 transition-colors shrink-0" />
          </div>
          <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors mb-1.5 leading-tight">
            {resource.title}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed flex-1">
            {resource.description}
          </p>
        </CardContent>
      </Card>
    </a>
  );
};

const ResourcesHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || null);

  useEffect(() => {
    const cat = searchParams.get('cat');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    if (catId) {
      setSearchParams({ cat: catId });
    } else {
      setSearchParams({});
    }
  };

  const filteredResources = useMemo(() => {
    let resources = RESOURCES;

    if (activeCategory) {
      resources = resources.filter(r => r.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      resources = resources.filter(r =>
        r.title.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.tags.some(t => t.includes(q))
      );
    }

    return resources;
  }, [searchQuery, activeCategory]);

  const activeCategoryData = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="resources-hub">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Veterans Resources</h1>
        <p className="text-sm text-slate-500">48 tools, calculators, and guides — your benefits, decoded</p>
      </div>

      {/* Quick References */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {QUICK_REFERENCES.map((ref) => (
          <a
            key={ref.title}
            href={ref.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`quick-ref-${ref.title.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
          >
            <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
              <ref.icon className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 truncate">{ref.title}</p>
              <p className="text-[10px] text-slate-500 truncate">{ref.description}</p>
            </div>
          </a>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="resources-search"
          placeholder="Search tools, calculators, and guides..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-10 text-sm border-slate-200 focus:border-blue-400"
        />
      </div>

      {/* Category Filter */}
      {activeCategory ? (
        <div className="mb-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCategoryChange(null)}
            className="text-slate-600 hover:text-slate-900 mb-3 -ml-2"
            data-testid="back-to-all"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All Categories
          </Button>
          <div className={`bg-gradient-to-r ${activeCategoryData.color} rounded-lg p-4 text-white`}>
            <div className="flex items-center gap-3">
              <activeCategoryData.icon className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-bold">{activeCategoryData.title}</h2>
                <p className="text-sm opacity-90">{activeCategoryData.description}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {CATEGORIES.map(cat => {
            const count = RESOURCES.filter(r => r.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                data-testid={`category-${cat.id}`}
                className={`bg-gradient-to-r ${cat.color} rounded-lg p-3 text-white text-left hover:opacity-90 transition-opacity group`}
              >
                <div className="flex items-center justify-between mb-1">
                  <cat.icon className="w-5 h-5" />
                  <ChevronRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="text-sm font-bold leading-tight">{cat.title}</h3>
                <p className="text-xs opacity-80 mt-0.5">{count} resources</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Results Count */}
      {searchQuery && (
        <p className="text-xs text-slate-500 mb-3" data-testid="search-results-count">
          {filteredResources.length} result{filteredResources.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Resources Grid */}
      {!activeCategory && !searchQuery ? (
        // Show all categories with their resources
        CATEGORIES.map(cat => {
          const catResources = RESOURCES.filter(r => r.category === cat.id);
          return (
            <div key={cat.id} className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <cat.icon className="w-4 h-4 text-slate-600" />
                  <h2 className="text-sm font-bold text-slate-800">{cat.title}</h2>
                  <Badge variant="secondary" className="text-xs">{catResources.length}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCategoryChange(cat.id)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  View all <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {catResources.slice(0, 4).map(resource => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filteredResources.map(resource => (
            <ResourceCard key={resource.id} resource={resource} />
          ))}
        </div>
      )}

      {filteredResources.length === 0 && (
        <div className="text-center py-12" data-testid="no-results">
          <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No resources found matching your search.</p>
          <Button
            variant="link"
            className="text-blue-600 text-sm mt-1"
            onClick={() => { setSearchQuery(''); handleCategoryChange(null); }}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Key Contacts */}
      <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Key Contacts</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
          <div><span className="font-medium">Veterans Crisis Line:</span> 988 (press 1)</div>
          <div><span className="font-medium">VA Benefits:</span> 1-800-827-1000</div>
          <div><span className="font-medium">TRICARE:</span> 1-800-444-5445</div>
          <div><span className="font-medium">VA Health:</span> 1-877-222-8387</div>
          <div><span className="font-medium">DFAS (Pay):</span> 1-888-332-7411</div>
          <div><span className="font-medium">GI Bill:</span> 1-888-442-4551</div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesHub;
